// src/firebase/auth.js - Google Authentication with role management
import { 
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  onAuthStateChanged
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, googleProvider, db } from './config';

// Google Sign In
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Check if user exists in Firestore
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // Create new user profile (default to student)
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: 'student',
        studentProfile: {
          university: '',
          graduationYear: new Date().getFullYear(),
          skills: [],
          certificatesEarned: 0,
          testsCompleted: 0
        },
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp(),
        isActive: true
      };
      
      await setDoc(userDocRef, userData);
      return { user, userData, isNewUser: true };
    } else {
      // Update last active
      await updateDoc(userDocRef, {
        lastActive: serverTimestamp(),
        isActive: true
      });
      
      const userData = userDoc.data();
      return { user, userData, isNewUser: false };
    }
  } catch (error) {
    console.error('Google sign in error:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Email/Password Sign Up
export const signUpWithEmail = async (email, password, additionalData) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;
    
    // Update display name
    await updateProfile(user, {
      displayName: additionalData.name
    });
    
    // Create user profile
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: additionalData.name,
      photoURL: '',
      role: additionalData.role || 'student',
      ...(additionalData.role === 'student' ? {
        studentProfile: {
          university: additionalData.university || '',
          graduationYear: additionalData.graduationYear || new Date().getFullYear(),
          skills: [],
          certificatesEarned: 0,
          testsCompleted: 0
        }
      } : {}),
      ...(additionalData.role === 'college' ? {
        collegeProfile: {
          collegeName: additionalData.collegeName || '',
          address: additionalData.address || '',
          website: additionalData.website || '',
          logoURL: '',
          signatureURL: '',
          verificationStatus: 'pending',
          testsCreated: 0,
          certificatesIssued: 0
        }
      } : {}),
      createdAt: serverTimestamp(),
      lastActive: serverTimestamp(),
      isActive: true
    };
    
    await setDoc(doc(db, 'users', user.uid), userData);
    return { user, userData, isNewUser: true };
  } catch (error) {
    console.error('Email sign up error:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Email/Password Sign In
export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;
    
    // Get user data
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;
    
    if (userData) {
      // Update last active
      await updateDoc(doc(db, 'users', user.uid), {
        lastActive: serverTimestamp(),
        isActive: true
      });
    }
    
    return { user, userData, isNewUser: false };
  } catch (error) {
    console.error('Email sign in error:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
};

// Sign Out
export const signOut = async () => {
  try {
    if (auth.currentUser) {
      // Update user status to inactive
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        lastActive: serverTimestamp(),
        isActive: false
      });
    }
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

// Auth State Listener
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.exists() ? userDoc.data() : null;
        callback({ user, userData });
      } catch (error) {
        console.error('Error fetching user data:', error);
        callback({ user, userData: null });
      }
    } else {
      callback({ user: null, userData: null });
    }
  });
};

// Update User Profile
export const updateUserProfile = async (userId, updates) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      lastActive: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Profile update error:', error);
    throw error;
  }
};

// Check if user can create tests (for college role)
export const canCreateTests = (userData) => {
  return userData?.role === 'college' && 
         userData?.collegeProfile?.verificationStatus === 'approved';
};

// Check if user is admin
export const isAdmin = (userData) => {
  return userData?.role === 'admin';
};

// Check if user is student
export const isStudent = (userData) => {
  return userData?.role === 'student';
};

// Get user role display name
export const getRoleDisplayName = (role) => {
  const roleMap = {
    'student': '👨‍🎓 Student',
    'college': '🏛️ College',
    'admin': '⚙️ Admin'
  };
  return roleMap[role] || 'User';
};

// Auth error message mapping
const getAuthErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'An account already exists with this email address.',
    'auth/weak-password': 'Password should be at least 6 characters long.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign in was cancelled.',
    'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups for this site.',
    'auth/cancelled-popup-request': 'Sign in was cancelled.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'default': 'An error occurred during authentication. Please try again.'
  };
  
  return errorMessages[errorCode] || errorMessages['default'];
};

// Password validation
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 6) {
    errors.push('Password must be at least 6 characters long');
  }
  if (!/(?=.*[a-z])/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (!/(?=.*\d)/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Email validation
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Generate username from email
export const generateUsername = (email) => {
  return email.split('@')[0].toLowerCase();
};

export default {
  signInWithGoogle,
  signUpWithEmail,
  signInWithEmail,
  signOut,
  onAuthStateChange,
  updateUserProfile,
  canCreateTests,
  isAdmin,
  isStudent,
  getRoleDisplayName,
  validatePassword,
  validateEmail,
  generateUsername
};